import { useCallback, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import { DataType, GeckoComponentDataFrame } from 'types'

import { CategoricalSummaryStatistics } from './categoricalStats/categoricalStatisticsDataType'
import { useCategoricalDataSummaryStatsFetcher } from './categoricalStats/useCategoricalDataSummaryStatsFetcher'
import { QuantitativeSummaryStatistics } from './quantitativeStats/quantitativeStatisticsDataType'
import { useQuantitativeDataStatsFetcher } from './quantitativeStats/useQuantitativeDataSummaryStats'

/**
 * A hook that returns a function which, when called, fetches the summary statistics for a given CDF.  The returned
 * summary statistics will be either categorical or quantitative, depending on the data type of the CDF (cdf.data.params.measurement_unit).
 */
export function useCdfSummaryStatsFetcher(): (
  cdf: GeckoComponentDataFrame
) => Promise<
  CategoricalSummaryStatistics | QuantitativeSummaryStatistics | undefined
> {
  const categoricalStatsFetcher = useCategoricalDataSummaryStatsFetcher()
  const quantitativeStatsFetcher = useQuantitativeDataStatsFetcher()

  return useCallback(
    async (
      cdf: GeckoComponentDataFrame
    ): Promise<
      CategoricalSummaryStatistics | QuantitativeSummaryStatistics | undefined
    > => {
      if (cdf.data.params.measurement_unit.dataType === DataType.Categorical) {
        return await categoricalStatsFetcher(cdf)
      }
      return await quantitativeStatsFetcher(cdf)
    },
    [categoricalStatsFetcher, quantitativeStatsFetcher]
  )
}

/**
 * A hook that fetches the summary statistics for a given CDF using SWR.
 * This hook exists to support legacy functionality -- any new code should use the `useCdfSummaryStatsFetcher` hook
 */
export function useFetchSummaryStatsSwr(
  cdf: GeckoComponentDataFrame | undefined
) {
  const baseFetcher = useCdfSummaryStatsFetcher()
  const noParamsFetcher = useCallback(async () => {
    if (!cdf) return undefined
    return {
      stats: await baseFetcher(cdf),
      id: cdf.id,
      generation: cdf.generation,
    }
  }, [baseFetcher, cdf])

  const { data, ...rest } = useSWRImmutable(
    [cdf?.id, cdf?.generation, 'global-summary-stats'],
    noParamsFetcher,
    {
      compare: (a, b) =>
        (a?.id ?? '') + (a?.generation?.toString() ?? '') ===
        (b?.id ?? '') + (b?.generation?.toString() ?? ''),
    }
  )

  return useMemo(
    () => ({
      data: data?.stats,
      ...rest,
    }),
    [data, rest]
  )
}
